import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, useIsMutating, keepPreviousData } from '@tanstack/react-query';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { BottomSheet } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { PayoutLogo } from '@/components/ui/payout-logo';
import { useEffect, useRef, useState } from 'react';
import { getTgUser, getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  ArrowDown,
  Gift,
  CheckCircle2,
  Clock,
  AlertCircle,
  ExternalLink,
  Ticket,
  Copy,
  Check,
  Repeat,
} from 'lucide-react';
import { toast } from 'sonner';
import { MoneySwitch } from '@/components/SectionSwitch';

export const Route = createFileRoute('/finance')({ component: Finance });

/** Ключ заявки на вывод: по нему страница знает, что запрос ещё летит. */
const WITHDRAW_MUTATION_KEY = ['partner-withdraw'];

function Finance() {
  const { t } = useTranslation();
  const { data: partner } = useQuery({
    queryKey: ['partner'],
    queryFn: () => api.me(),
    placeholderData: keepPreviousData,
  });
  const { data: txs } = useQuery({
    queryKey: ['txs'],
    queryFn: () => api.walletTransactions(),
    placeholderData: keepPreviousData,
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const balance = partner?.balanceRub ?? 0;
  const held = partner?.heldRub ?? 0;
  const available = Math.max(0, balance - held);

  if (!partner) return <FinanceSkeleton />;

  const history = txs || [];

  return (
    <div className="p-4 space-y-5 pb-24">
      <MoneySwitch />
      {/* Баланс. Спокойная карточка, как «К выводу» на сводке: зелёный только у денег
          со знаком «+», а не фоном всей карточки. */}
      <div className="surface-card relative overflow-hidden rounded-[22px] p-5">
        <div className="text-[12.5px] text-dim">{t('Finance balance')}</div>
        <div className="mt-1.5 font-display text-[40px] font-light leading-none tracking-[-0.02em] nums">
          {/* Минус красным, как на сводке. */}
          <span className={balance < 0 ? 'text-danger' : undefined}>{Math.round(Number.isFinite(balance) ? balance : 0).toLocaleString('ru')}</span>{' '}
          <span className={balance < 0 ? 'text-danger' : 'text-muted-foreground'}>₽</span>
        </div>
        {held > 0 && (
          <div className="relative mt-3 rounded-[14px] bg-amber-500/10 border border-amber-500/25 px-3.5 py-2.5 text-[12.5px] leading-snug text-[#f5d38a]">
            <div className="font-bold">
              Заморожено {held.toLocaleString('ru')} ₽, к выводу {available.toLocaleString('ru')} ₽
            </div>
            <div className="mt-0.5 text-[#e2c27a]/85">
              {partner.holdBots
                .map((b) => `@${b.username}: ${b.subscriptions} живых подписок, бот выключен`)
                .join('; ')}
              . Включите бота, и холд снимется сам.
            </div>
          </div>
        )}
        {partner.isDelegate ? (
          <div className="relative mt-[18px] rounded-[16px] bg-white/[0.05] border border-white/[0.08] px-4 py-3 text-[13px] text-dim">
            Вывод и автовывод настраивает владелец кабинета.
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setSheetOpen(true);
              }}
              className="relative w-full mt-[18px] inline-flex items-center justify-center gap-2 btn-glass-red font-semibold text-[15.5px] py-[15px] rounded-[16px] active:scale-[0.985] transition-transform"
            >
              <ArrowDown className="w-[18px] h-[18px]" />
              {t('Finance withdraw')}
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setAutoOpen(true);
              }}
              className="relative w-full mt-2.5 inline-flex items-center justify-center gap-2 text-[#cfd2d8] font-bold text-[14px] py-[13px] rounded-[16px] bg-white/[0.06] border border-white/[0.08] active:scale-[0.985] transition-transform"
            >
              <Repeat className="w-[16px] h-[16px]" />
              Настроить автовывод
            </button>
          </>
        )}
      </div>

      {/* История операций */}
      <div>
        <div className="text-[12.5px] text-dim mb-2.5 px-1">{t('History')}</div>
        {history.length === 0 ? (
          <div className="surface-card rounded-[22px] py-11 px-6 text-center">
            <div className="w-[54px] h-[54px] rounded-[17px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
              <Wallet className="w-[26px] h-[26px]" strokeWidth={1.5} />
            </div>
            <div className="font-bold text-[15px] text-muted-foreground">Пока пусто</div>
            <div className="text-[12.5px] text-dim mt-1.5">Здесь появятся продажи, бонусы и выводы.</div>
          </div>
        ) : (
          <div className="flex flex-col gap-[9px]">
            {history.map((tx) => (
              <div key={tx.id} className="surface-card rounded-[16px] p-[14px] flex items-center gap-3">
                <TxIcon kind={tx.kind} />
                <div className="flex-1 min-w-0">
                  {/* Две строки: «Возмещение за вывод клиента» и «Реферальные бонусы клиентам»
                      обрывались на полуслове уже на 375px. */}
                  <div className="font-bold text-[13.5px] leading-snug line-clamp-2">{tx.description}</div>
                  <div className="text-[11px] text-dim truncate mt-0.5">
                    {new Date(tx.timestamp).toLocaleString('ru')}
                    {/* Операции с внесённых счётом денег помечаем: иначе непонятно,
                        почему сумма к выводу не изменилась. */}
                    {tx.pocket === 'paid' && ' · со счёта'}
                  </div>
                </div>
                <span
                  className={`font-display font-bold text-[15px] nums whitespace-nowrap ${
                    tx.amountRub >= 0 ? 'text-[#38e0a5]' : 'text-[#f87171]'
                  }`}
                >
                  {tx.amountRub >= 0 ? '+' : ''}
                  {tx.amountRub.toLocaleString('ru')} ₽
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title={t('Finance withdraw')}
      >
        <WithdrawSheetBody onClose={() => setSheetOpen(false)} max={available} />
      </BottomSheet>

      <BottomSheet
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        title="Автовывод"
      >
        <AutoPayoutSheetBody onClose={() => setAutoOpen(false)} />
      </BottomSheet>
    </div>
  );
}

function TxIcon({ kind }: { kind: 'in' | 'out' | 'withdraw' | 'referral' }) {
  if (kind === 'in')
    return (
      <div className="w-9 h-9 rounded-[11px] bg-[rgba(56,224,165,0.14)] text-[#38e0a5] flex items-center justify-center shrink-0">
        <ArrowDownRight className="w-[18px] h-[18px]" />
      </div>
    );
  if (kind === 'referral')
    return (
      <div className="w-9 h-9 rounded-[11px] bg-[rgba(159,122,234,0.16)] text-[#b29bf0] flex items-center justify-center shrink-0">
        <Gift className="w-[18px] h-[18px]" />
      </div>
    );
  return (
    <div className="w-9 h-9 rounded-[11px] bg-[rgba(248,113,113,0.14)] text-[#f87171] flex items-center justify-center shrink-0">
      <ArrowUpRight className="w-[18px] h-[18px]" />
    </div>
  );
}

type WithdrawResult = {
  auto_executed?: boolean;
  external_ref?: string | null;
  auto_error?: string | null;
  transfer_state?: 'sending' | 'sent' | 'unknown' | null;
  method: string;
  status: string;
  amount_rub: number;
};

type PayoutMethodCfg = {
  method: string;
  label: string;
  fixed_fee_usd_minor: number;
  percent_fee_bp: number;
  destination_hint: string;
  min_payout_usd_minor: number;
  /** Минимум вывода в копейках рубля по текущему курсу, не меньше 100 ₽ (заявка #124). */
  min_payout_display_minor: number;
  note?: string;
  fee_quote_required?: boolean;
  supports_memo?: boolean;
};

const AUTO_INTERVALS = [
  { hours: 6, label: '6 часов' },
  { hours: 12, label: '12 часов' },
  { hours: 24, label: 'сутки' },
  { hours: 72, label: '3 дня' },
  { hours: 168, label: 'неделю' },
];

/**
 * Автовывод по порогу: накопилось N рублей → уходит само, но не чаще заданного.
 *
 * Показываем только способы с реальным автоисполнением (их отдаёт бэк в
 * `available_methods`). СБП и USDT-сети сюда не попадают: они исполняются вручную,
 * и «автовывод» на них означал бы поток заявок, которые всё равно жмут руками.
 */
function AutoPayoutSheetBody({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ['auto-payout'], queryFn: () => api.autoPayout() });
  const { data: methods } = useQuery({ queryKey: ['payout-methods'], queryFn: () => api.payoutMethods() });

  const [enabled, setEnabled] = useState(false);
  const [thresholdStr, setThresholdStr] = useState('');
  const [method, setMethod] = useState('cryptobot');
  const [destination, setDestination] = useState('');
  const [pinMode, setPinMode] = useState('self');
  const [interval, setIntervalHours] = useState(24);
  const [hydrated, setHydrated] = useState(false);

  // Подставляем сохранённое ровно один раз: иначе рефетч затирал бы то, что
  // партнёр печатает прямо сейчас.
  useEffect(() => {
    if (!data || hydrated) return;
    setEnabled(data.enabled);
    setThresholdStr(
      data.threshold_display_minor != null ? String(Math.round(data.threshold_display_minor / 100)) : '',
    );
    setMethod(data.method ?? data.available_methods[0] ?? 'cryptobot');
    setDestination(data.destination ?? '');
    setPinMode(data.pin_mode ?? 'self');
    setIntervalHours(data.min_interval_hours);
    setHydrated(true);
  }, [data, hydrated]);

  const save = useMutation({
    mutationFn: () =>
      api.saveAutoPayout({
        enabled,
        threshold_display_minor: thresholdStr ? Math.round(Number(thresholdStr) * 100) : null,
        method,
        destination,
        pin_mode: method === 'cryptobot' ? pinMode : null,
        min_interval_hours: interval,
      }),
    onSuccess: () => {
      haptic('success');
      toast.success(enabled ? 'Автовывод включён' : 'Автовывод выключен');
      qc.invalidateQueries({ queryKey: ['auto-payout'] });
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      const body = (e as { body?: { error?: { message?: string } } }).body;
      toast.error(body?.error?.message ?? 'Не удалось сохранить');
    },
  });

  if (isPending || !data) return <SkeletonCard className="h-[220px] rounded-[18px]" />;

  const available = (methods ?? []).filter((m) => data.available_methods.includes(m.method));
  // Минимум выбранного способа, тот же, что у ручного вывода (заявка #124). Пока список
  // способов не пришёл, берём порог, который бэк посчитал для сохранённого способа.
  const methodMin = available.find((m) => m.method === method)?.min_payout_display_minor;
  const minRub = Math.ceil((methodMin ?? data.min_threshold_display_minor) / 100);
  const capRub = Math.floor(data.auto_execution_cap_display_minor / 100);
  const thresholdRub = Number(thresholdStr || 0);
  // xRocket переводит на Telegram самого партнёра: реквизитов нет вовсе.
  const needsDestination = method !== 'xrocket' && (method !== 'cryptobot' || pinMode === 'username');
  const canSave =
    !enabled || (thresholdRub >= minRub && (!needsDestination || destination.trim().length > 0));

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-[15px] text-foreground">Выводить автоматически</div>
          <div className="text-[12px] text-dim mt-0.5 leading-snug">
            Когда на балансе накопится нужная сумма, вывод уйдёт сам.
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setEnabled((v) => !v);
          }}
          role="switch"
          aria-checked={enabled}
          aria-label="Выводить автоматически"
          className={`relative shrink-0 w-[52px] h-[30px] rounded-full transition-colors after:absolute after:-inset-2 after:content-[''] ${
            enabled ? 'bg-[#1fb87f]' : 'bg-white/[0.12]'
          }`}
        >
          <span
            className={`block w-[24px] h-[24px] rounded-full bg-white transition-transform mt-[3px] ${
              enabled ? 'translate-x-[25px]' : 'translate-x-[3px]'
            }`}
          />
        </button>
      </div>

      {data.last_error && (
        <div className="rounded-[14px] px-3 py-2.5 text-[12px] leading-snug bg-[rgba(240,101,106,0.1)] border border-[rgba(240,101,106,0.25)] text-[#f0a0a3]">
          Последняя попытка не прошла: {data.last_error}
        </div>
      )}

      {enabled && (
        <>
          <div className="space-y-1.5">
            <div className="text-[11px] text-dim uppercase tracking-wider font-bold">Выводить от, ₽</div>
            <input
              type="number"
              inputMode="numeric"
              value={thresholdStr}
              onChange={(e) => setThresholdStr(e.target.value)}
              placeholder={String(minRub)}
              className="w-full rounded-[14px] px-3.5 py-3 text-[15px] font-mono bg-white/[0.05] text-foreground"
            />
            <div className="text-[11px] text-dim leading-snug">
              Минимум {minRub.toLocaleString('ru')} ₽. Уходит весь доступный баланс, комиссия
              удерживается из суммы.
              {thresholdRub > capRub && (
                <span className="block text-[#f0b04e] mt-1">
                  Выше {capRub.toLocaleString('ru')} ₽ перевод не уйдёт сам: заявку придётся
                  подтверждать вручную.
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="text-[11px] text-dim uppercase tracking-wider font-bold">Куда</div>
            <div className="grid grid-cols-2 gap-2">
              {available.map((m) => (
                <button
                  key={m.method}
                  type="button"
                  onClick={() => {
                    haptic('light');
                    setMethod(m.method);
                  }}
                  className={`rounded-[14px] px-3 py-2.5 text-left text-[13px] font-semibold border transition-colors ${
                    method === m.method
                      ? 'border-[rgba(56,224,165,0.4)] bg-[rgba(56,224,165,0.1)] text-foreground'
                      : 'border-white/[0.08] bg-white/[0.04] text-muted-foreground'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <PayoutLogo method={m.method} className="size-7" />
                    {m.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {method === 'cryptobot' && (
            <div className="space-y-1.5">
              <div className="text-[11px] text-dim uppercase tracking-wider font-bold">Чек CryptoBot</div>
              <div className="flex gap-2">
                {[
                  { v: 'self', l: 'Мне' },
                  { v: 'open', l: 'На предъявителя' },
                  { v: 'username', l: 'На логин' },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setPinMode(o.v)}
                    className={`flex-1 rounded-[12px] py-2 text-[12px] font-semibold border ${
                      pinMode === o.v
                        ? 'border-[rgba(56,224,165,0.4)] bg-[rgba(56,224,165,0.1)] text-foreground'
                        : 'border-white/[0.08] bg-white/[0.04] text-muted-foreground'
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {needsDestination && (
            <div className="space-y-1.5">
              <div className="text-[11px] text-dim uppercase tracking-wider font-bold">Реквизиты</div>
              <input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={available.find((m) => m.method === method)?.destination_hint ?? ''}
                className="w-full rounded-[14px] px-3.5 py-3 text-[14px] bg-white/[0.05] text-foreground"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <div className="text-[11px] text-dim uppercase tracking-wider font-bold">Не чаще раза в</div>
            <div className="grid grid-cols-5 gap-1.5">
              {AUTO_INTERVALS.map((o) => (
                <button
                  key={o.hours}
                  type="button"
                  onClick={() => setIntervalHours(o.hours)}
                  className={`rounded-[12px] py-2 text-[11px] font-semibold border ${
                    interval === o.hours
                      ? 'border-[rgba(56,224,165,0.4)] bg-[rgba(56,224,165,0.1)] text-foreground'
                      : 'border-white/[0.08] bg-white/[0.04] text-muted-foreground'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <button
        type="button"
        disabled={!canSave || save.isPending}
        onClick={() => save.mutate()}
        className="w-full py-[14px] rounded-[16px] font-semibold text-[15px] btn-glass-red disabled:opacity-50 active:scale-[0.985] transition-transform"
      >
        {save.isPending ? 'Сохраняем…' : 'Сохранить'}
      </button>
    </div>
  );
}

const QUOTE_BUSY = 'Считаем комиссию…';

function WithdrawSheetBody({ onClose, max }: { onClose: () => void; max: number }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Шторку можно закрыть, пока заявка летит, а открытая заново шторка — новый
  // компонент со своим `isPending=false`. Раньше так уходила вторая заявка: человек
  // не видел, что первая создана. Теперь повтор ждёт любую заявку в полёте, а об
  // успехе после закрытия скажет тост.
  const inFlight = useIsMutating({ mutationKey: WITHDRAW_MUTATION_KEY }) > 0;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  // Храним как строку — иначе React-controlled type=number баг: префилл нельзя стереть.
  const [amountStr, setAmountStr] = useState<string>('');
  const [method, setMethod] = useState<string>('cryptobot');
  const [destination, setDestination] = useState('');
  // Кому достанется чек CryptoBot. Без явного режима бэк считает заявку выводом
  // «себе» и привязывает чек к Telegram заказчика — введённый логин получателя
  // при этом никуда не уходит.
  const [pinMode, setPinMode] = useState<'self' | 'open' | 'username'>('self');
  const myUsername = getTgUser()?.username ?? null;
  const [result, setResult] = useState<WithdrawResult | null>(null);
  // 'payment' — комиссия съест часть выплаты (input=gross, на руку=gross-fee).
  // 'balance' — комиссия сверху из баланса (input=net, баланс минус gross=net+fee).
  const [feeCharge, setFeeCharge] = useState<'payment' | 'balance'>('payment');

  const { data: methods = [], isPending: methodsPending } = useQuery({
    queryKey: ['payout-methods'],
    queryFn: () => api.payoutMethods(),
    staleTime: 60 * 60 * 1000,
  });
  // Live курс CBR для конверсии fee из USD→RUB.
  const { data: stats } = useQuery({
    queryKey: ['partner-dashboard-stats'],
    queryFn: () => api.dashboardStats(),
  });
  const fxRate = stats?.fx_rate ?? 100;

  const currentMethod: PayoutMethodCfg | undefined = methods.find((m) => m.method === method);
  // Комментарий к переводу: только USDT в сети TON (адрес на бирже).
  const [memo, setMemo] = useState('');

  const amount = parseInt(amountStr, 10);
  const amountValid = Number.isFinite(amount) && amount > 0;

  // Считаем gross/net по выбранному feeCharge:
  // - 'payment': input = gross (списывается), net = gross − fee.
  // - 'balance': input = net (получит), gross = (net + fixed) / (1 − pct/10000), баланс минус gross.
  const fixedFeeRub = currentMethod
    ? Math.round((currentMethod.fixed_fee_usd_minor / 100) * fxRate)
    : 0;
  const pctBp = currentMethod?.percent_fee_bp ?? 0;
  // Выплата через 2328: комиссия сети, сумму к получению считает сервер. Спрашиваем
  // с паузой после ввода, чтобы не дёргать котировку на каждую цифру.
  const quoted = !!currentMethod?.fee_quote_required;
  const amountMinor = amountValid ? amount * 100 : 0;
  const [quoteAmount, setQuoteAmount] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setQuoteAmount(amountMinor), 400);
    return () => clearTimeout(t);
  }, [amountMinor]);
  const quoteQ = useQuery({
    queryKey: ['payout-quote', method, quoteAmount, feeCharge],
    queryFn: () => api.payoutQuote(method, quoteAmount, feeCharge),
    enabled: quoted && quoteAmount > 0,
    staleTime: 30_000,
    retry: false,
  });
  // Котировка относится к тому, что сейчас в поле: пока пауза не прошла, её нет.
  const quote = quoted && quoteAmount === amountMinor ? quoteQ.data : undefined;
  let grossRub = 0;
  let netRub = 0;
  let feeRub = 0;
  if (amountValid && currentMethod && quoted) {
    // 2328: суммы из котировки сервера, формулой их здесь не посчитать.
    grossRub = Math.round((quote?.gross_display_minor ?? 0) / 100);
    feeRub = Math.round((quote?.fee_display_minor ?? 0) / 100);
    netRub = Math.max(0, Math.round((quote?.net_display_minor ?? 0) / 100));
  } else if (amountValid && currentMethod) {
    if (feeCharge === 'payment') {
      grossRub = amount;
      feeRub = fixedFeeRub + Math.round((amount * pctBp) / 10000);
      netRub = Math.max(0, grossRub - feeRub);
    } else {
      // balance: net = amount, gross = (net + fixed) / (1 - pct/10000)
      const denom = (10000 - pctBp) / 10000;
      grossRub = denom > 0 ? Math.ceil((amount + fixedFeeRub) / denom) : amount + fixedFeeRub;
      feeRub = grossRub - amount;
      netRub = amount;
    }
  }

  const destClean = destination.trim();
  const isCryptobot = method === 'cryptobot';
  // У CryptoBot реквизиты нужны только в режиме «на логин»: «себе» и «по ссылке»
  // обходятся без них.
  const needsDestination = method !== 'xrocket' && (!isCryptobot || pinMode === 'username');
  const destValid = !needsDestination || destClean.length >= 4;
  // Минимум приходит с сервера по каждому способу в копейках, по тому же курсу, что и
  // заявка: от 100 ₽, у СБП и USDT-сетей их $10 по курсу (заявка #124). Сумма здесь
  // вводится целыми рублями, поэтому округляем вверх и сравниваем с введённой суммой.
  const minRubOf = (m: PayoutMethodCfg) => Math.ceil(m.min_payout_display_minor / 100);
  // У 2328 минимум ещё и у сети (минимум перевода плюс комиссия), его знает котировка.
  const methodMinRub = currentMethod
    ? Math.max(minRubOf(currentMethod), Math.ceil((quote?.min_display_minor ?? 0) / 100))
    : null;
  // На балансе меньше самого низкого минимума: вывести нельзя ни одним способом.
  const lowestMinRub = methods.length > 0 ? Math.min(...methods.map(minRubOf)) : null;
  const belowMin = lowestMinRub !== null && max < lowestMinRub;
  const error =
    !amountValid ? 'Введите сумму'
    : methodMinRub === null ? 'Выберите способ вывода'
    : amount < methodMinRub ? `Минимум ${methodMinRub.toLocaleString('ru')} ₽`
    : grossRub > max ? `Не хватает на балансе (нужно ${grossRub.toLocaleString('ru')} ₽)`
    : !destValid ? (isCryptobot ? 'Укажите логин получателя в CryptoBot' : 'Укажите реквизиты для вывода')
    : quoted && quoteQ.isError && quoteAmount === amountMinor
      ? (quoteQ.error instanceof Error && quoteQ.error.message) ||
        'Не получилось посчитать комиссию. Попробуйте через минуту.'
    : quoted && !quote ? QUOTE_BUSY
    : netRub <= 0 ? `Сумма меньше комиссии (${feeRub.toLocaleString('ru')} ₽)`
    : null;

  const { mutate: submitWithdraw, isPending } = useMutation({
    mutationKey: WITHDRAW_MUTATION_KEY,
    mutationFn: () =>
      api.requestWithdraw({
        amount_rub: amount,
        method,
        destination: destClean,
        fee_charge: feeCharge,
        ...(isCryptobot ? { cryptobot_pin_mode: pinMode } : {}),
        ...(currentMethod?.supports_memo && memo.trim() ? { memo: memo.trim() } : {}),
      }),
    onSuccess: (resp) => {
      haptic('success');
      if (!mountedRef.current) toast.success('Заявка на вывод создана. Статус в разделе «История выводов».');
      setResult({
        auto_executed: resp.auto_executed,
        external_ref: resp.external_ref,
        auto_error: resp.auto_error,
        transfer_state: resp.transfer_state,
        method,
        status: resp.status,
        amount_rub: amount,
      });
      // Сразу инвалидируем — баланс/транзакции/payouts обновятся фоном пока юзер
      // смотрит на success-экран. К моменту закрытия sheet'а в UI новые цифры.
      qc.invalidateQueries({ queryKey: ['partner'] });
      qc.invalidateQueries({ queryKey: ['partner-me'] });
      qc.invalidateQueries({ queryKey: ['txs'] });
      qc.invalidateQueries({ queryKey: ['payouts'] });
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось отправить заявку');
    },
  });

  const submit = () => {
    if (error || inFlight) return;
    submitWithdraw();
  };

  // Чипы суммы — рендерим .map() по массиву (одиночные элементы отставали в подсветке).
  const chips: { label: string; value: number; disabled?: boolean }[] =
    methodMinRub === null
      ? []
      : [
          { label: `${methodMinRub.toLocaleString('ru')} ₽`, value: methodMinRub, disabled: max < methodMinRub },
          { label: 'Половина', value: Math.floor(max / 2), disabled: max < methodMinRub },
          { label: `Всё · ${max.toLocaleString('ru')}`, value: max, disabled: max < methodMinRub },
        ];

  if (result) return <WithdrawResultView result={result} onClose={onClose} />;
  // Без списка способов минимума не знаем: «меньше минимума» по догадке соврал бы.
  if (methodsPending) return <SkeletonCard className="h-[320px] rounded-[18px]" />;
  if (belowMin) return <BelowMinView max={max} min={lowestMinRub} onClose={onClose} />;

  return (
    <div className="space-y-5">
      {/* Сумма */}
      <div className="surface-inset rounded-[18px] px-[18px] py-4">
        <div className="flex items-baseline gap-1.5">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="0"
            className="flex-1 min-w-0 bg-transparent border-0 border-b-2 border-[rgba(56,224,165,0.45)] focus:border-[#38e0a5] outline-none font-display font-bold text-[32px] tracking-[-1px] nums pb-0.5"
          />
          <span className="font-display font-bold text-[32px] text-[#5fe3ad]">₽</span>
        </div>
        {methodMinRub !== null && (
          <div className={`text-[11.5px] mt-2 ${max < methodMinRub ? 'text-[#f0b04e]' : 'text-dim'}`}>
            {max < methodMinRub
              ? `Минимум для этого способа ${methodMinRub.toLocaleString('ru')} ₽, на балансе ${max.toLocaleString('ru')} ₽. Выберите другой способ.`
              : `Впишите сумму · от ${methodMinRub.toLocaleString('ru')} ₽ до ${max.toLocaleString('ru')} ₽`}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            disabled={c.disabled}
            onClick={() => {
              haptic('selection');
              setAmountStr(String(c.value));
            }}
            className="surface-inset rounded-[12px] py-2.5 text-xs font-bold active:scale-[0.97] transition-transform disabled:opacity-30"
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Способ — .map() */}
      <div>
        <div className="text-[12.5px] text-dim mb-2.5 px-0.5">Способ</div>
        <div className="grid grid-cols-2 gap-[9px]">
          {methods.map((m) => {
            const isSelected = method === m.method;
            const pctLabel = m.percent_fee_bp > 0 ? `${(m.percent_fee_bp / 100).toFixed(0)}%` : '';
            const fixedLabel = m.fixed_fee_usd_minor > 0 ? `$${(m.fixed_fee_usd_minor / 100).toFixed(2)}` : '';
            const feeLabel = m.fee_quote_required
              ? pctLabel
                ? `сеть + ${pctLabel}`
                : 'комиссия сети'
              : [fixedLabel, pctLabel].filter(Boolean).join(' + ') || 'без комиссии';
            return (
              <button
                type="button"
                key={m.method}
                onClick={() => {
                  haptic('selection');
                  setMethod(m.method);
                  setDestination('');
                  setMemo('');
                  setPinMode('self');
                }}
                className={`rounded-[14px] px-3 py-3 text-left transition-colors ${
                  isSelected
                    ? 'bg-[rgba(56,224,165,0.12)] border border-[#38e0a5] text-foreground'
                    : 'surface-inset text-muted-foreground'
                }`}
              >
                <div className="flex items-center gap-2">
                  <PayoutLogo method={m.method} />
                  <span className="font-bold text-[13px] leading-tight">{m.label}</span>
                </div>
                <div className="font-mono text-[11px] text-dim mt-1.5">{feeLabel}</div>
              </button>
            );
          })}
        </div>
        {currentMethod?.note && (
          <div className="mt-2.5 rounded-[12px] px-3.5 py-2.5 text-[12px] leading-snug bg-[rgba(251,191,36,0.08)] text-[#fbbf24] border border-[rgba(251,191,36,0.18)]">
            {currentMethod.note}
          </div>
        )}
      </div>

      {/* Кому достанется чек (только CryptoBot) */}
      {isCryptobot && (
        <div>
          <div className="text-[12.5px] text-dim mb-2.5 px-0.5">Получатель</div>
          <div className="grid grid-cols-3 gap-[9px]">
            {([
              { key: 'self', label: 'Мне', hint: 'В мой Telegram' },
              { key: 'username', label: 'По логину', hint: 'Другому человеку' },
              { key: 'open', label: 'По ссылке', hint: 'Активирует любой' },
            ] as const).map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => {
                  haptic('selection');
                  setPinMode(opt.key);
                  if (opt.key !== 'username') setDestination('');
                }}
                className={`rounded-[14px] px-3 py-2.5 text-left transition-colors ${
                  pinMode === opt.key
                    ? 'bg-[rgba(56,224,165,0.12)] border border-[#38e0a5] text-foreground'
                    : 'surface-inset text-muted-foreground'
                }`}
              >
                <div className="font-bold text-[13px] leading-tight">{opt.label}</div>
                <div className="font-mono text-[10px] text-dim mt-1">{opt.hint}</div>
              </button>
            ))}
          </div>
          {pinMode === 'open' && (
            <div className="mt-2.5 rounded-[12px] px-3.5 py-2.5 text-[12px] leading-snug bg-[rgba(251,191,36,0.08)] text-[#fbbf24] border border-[rgba(251,191,36,0.18)]">
              Чек активирует любой, кто откроет ссылку. Не публикуйте её.
            </div>
          )}
        </div>
      )}

      {/* Реквизиты */}
      {needsDestination && (
        <div>
          <div className="flex items-center justify-between mb-2.5 px-0.5">
            <span className="text-[12.5px] text-dim">
              {isCryptobot ? 'Логин получателя' : quoted ? 'Адрес кошелька' : 'Реквизиты'}
            </span>
            {/* Свой ник берём из initData: в мини-аппе он всегда актуальный, ходить
                за ним на бэкенд незачем. */}
            {isCryptobot && myUsername && (
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  setDestination(`@${myUsername}`);
                }}
                className="text-[11px] font-semibold text-primary-light"
              >
                Мой ник
              </button>
            )}
          </div>
          <input
            type="text"
            value={destination}
            // Адрес кошелька копируют с пробелами и переносами: в адресе их не бывает,
            // убираем сразу, а не отказом сервера.
            onChange={(e) =>
              setDestination(quoted ? e.target.value.replace(/\s+/g, '') : e.target.value)
            }
            spellCheck={false}
            autoComplete="off"
            placeholder={isCryptobot ? '@username' : (currentMethod?.destination_hint ?? '')}
            className="w-full surface-inset rounded-[14px] px-4 py-3.5 text-sm outline-none focus:border-[rgba(56,224,165,0.5)]"
          />
          {isCryptobot && (
            <div className="text-[11px] text-dim mt-2 px-0.5 leading-snug">
              Логин того, кто будет активировать чек в CryptoBot. Чек другому человеку не откроется.
            </div>
          )}
        </div>
      )}

      {currentMethod?.supports_memo && (
        <div>
          <div className="mb-2.5 px-0.5 text-[12.5px] text-dim">Комментарий (memo)</div>
          <input
            type="text"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            maxLength={255}
            spellCheck={false}
            autoComplete="off"
            placeholder="Необязательно"
            className="w-full surface-inset rounded-[14px] px-4 py-3.5 text-sm outline-none focus:border-[rgba(56,224,165,0.5)]"
          />
          <div className="text-[11px] text-dim mt-2 px-0.5 leading-snug">
            Нужен, если адрес на бирже: биржа покажет его рядом с адресом. Для своего кошелька оставьте пустым.
          </div>
        </div>
      )}

      {/* Кто платит комиссию */}
      <div>
        <div className="text-[12.5px] text-dim mb-2.5 px-0.5">Комиссия</div>
        <div className="grid grid-cols-2 gap-[9px]">
          <button
            type="button"
            onClick={() => setFeeCharge('payment')}
            className={`rounded-[14px] px-3 py-2.5 text-left transition-colors ${
              feeCharge === 'payment'
                ? 'bg-[rgba(56,224,165,0.12)] border border-[#38e0a5] text-foreground'
                : 'surface-inset text-muted-foreground'
            }`}
          >
            <div className="font-bold text-[13.5px]">С платежа</div>
            <div className="text-[11px] text-dim mt-0.5">Придёт меньше</div>
          </button>
          <button
            type="button"
            onClick={() => setFeeCharge('balance')}
            className={`rounded-[14px] px-3 py-2.5 text-left transition-colors ${
              feeCharge === 'balance'
                ? 'bg-[rgba(56,224,165,0.12)] border border-[#38e0a5] text-foreground'
                : 'surface-inset text-muted-foreground'
            }`}
          >
            <div className="font-bold text-[13.5px]">С баланса</div>
            <div className="text-[11px] text-dim mt-0.5">Придёт ровно</div>
          </button>
        </div>
      </div>

      {/* Live preview gross / fee / net */}
      {amountValid && currentMethod && (!quoted || quote) && (
        <div className="rounded-[16px] p-4 surface-card">
          <div className="flex items-center justify-between text-xs">
            <span className="text-dim">Спишется с баланса</span>
            <span className="font-semibold nums">{grossRub.toLocaleString('ru')} ₽</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1.5">
            <span className="text-dim">
              {quoted ? (pctBp > 0 ? `Комиссия сети + ${pctBp / 100}%` : 'Комиссия сети') : 'Комиссия'}
            </span>
            <span className="font-semibold text-[#f0b04e] nums">−{feeRub.toLocaleString('ru')} ₽</span>
          </div>
          <div className="flex items-center justify-between pt-2 mt-2 border-t border-white/[0.06]">
            <span className="text-[12.5px] text-dim">Придёт</span>
            <span className="font-display font-bold text-[22px] text-[#38e0a5] nums">≈ {netRub.toLocaleString('ru')} ₽</span>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!!error || isPending || inFlight}
        className="w-full inline-flex items-center justify-center gap-2 btn-glass-red font-semibold text-[15.5px] py-4 rounded-[18px] active:scale-[0.985] transition-transform disabled:opacity-40"
      >
        {isPending || inFlight ? 'Отправляю...' : 'Подтвердить вывод'}
      </button>
      {error && amountStr && (
        // «Считаем комиссию» — это состояние, а не ошибка: красным его не красим.
        <p className={`text-xs text-center -mt-2 ${error === QUOTE_BUSY ? 'text-dim' : 'text-[#f87171]'}`}>
          {error}
        </p>
      )}
    </div>
  );
}

function BelowMinView({ max, min, onClose }: { max: number; min: number; onClose: () => void }) {
  const need = min - max;
  return (
    <div className="space-y-4 py-2">
      <div className="flex flex-col items-center text-center gap-3 py-4">
        <div className="w-14 h-14 rounded-2xl bg-[rgba(240,176,78,0.15)] text-[#f0b04e] flex items-center justify-center">
          <Wallet className="w-7 h-7" />
        </div>
        <div>
          <h4 className="font-bold text-base">Пока недостаточно для вывода</h4>
          <p className="text-sm text-muted-foreground mt-1">
            На балансе <b>{max.toLocaleString('ru')} ₽</b>, а минимум для вывода <b>{min.toLocaleString('ru')} ₽</b>.
          </p>
          <p className="text-xs text-dim mt-2">
            Не хватает {need.toLocaleString('ru')} ₽. Вывод станет доступен после новых продаж.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="w-full bg-white/[0.07] text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
      >
        Понятно
      </button>
    </div>
  );
}

function WithdrawResultView({ result, onClose }: { result: WithdrawResult; onClose: () => void }) {
  const [checkCopied, setCheckCopied] = useState(false);
  const checkUrl = result.external_ref && result.external_ref.startsWith('http') ? result.external_ref : null;

  const openCheck = () => {
    if (!checkUrl) return;
    const tg = (window as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void; openLink?: (u: string) => void } } }).Telegram?.WebApp;
    if (tg?.openTelegramLink && checkUrl.includes('t.me/')) tg.openTelegramLink(checkUrl);
    else if (tg?.openLink) tg.openLink(checkUrl);
    else window.open(checkUrl, '_blank');
  };

  // Копирование ссылки чека — на случай если CryptoBot недоступен (забанен): партнёр
  // откроет ссылку в браузере/на другом устройстве.
  const copyCheckUrl = async () => {
    if (!checkUrl) return;
    try {
      await navigator.clipboard.writeText(checkUrl);
      setCheckCopied(true);
      haptic('success');
      toast.success('Ссылка чека скопирована');
      setTimeout(() => setCheckCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  // 3 case'а:
  //   1. auto-executed + check_url → большая зелёная карточка + CTA
  //   2. auto_error → жёлтый предупредительный экран
  //   3. fallback (pending без auto) → синяя «owner рассмотрит»
  if (result.auto_executed && checkUrl) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center space-y-3 py-4">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(56,224,165,0.14)] text-[#38e0a5] flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
            <p className="text-sm text-muted-foreground mt-1">
              Чек создан мгновенно. Откройте ссылку и активируйте его в @CryptoBot: USDT придёт на ваш кошелёк.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCheck}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <Ticket className="w-4 h-4" />
          Получить деньги в CryptoBot
          <ExternalLink className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={copyCheckUrl}
          className="w-full bg-white/[0.07] text-foreground py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          {checkCopied ? <Check className="w-4 h-4 text-[#38e0a5]" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
          Копировать ссылку чека
        </button>
        <button type="button" onClick={onClose} className="w-full bg-white/[0.07] text-foreground py-2.5 rounded-xl text-xs font-bold">
          Закрыть
        </button>
        <p className="text-[11px] text-center text-dim">Ссылка также сохранена в «История выводов».</p>
      </div>
    );
  }

  // xRocket не нашёл аккаунт партнёра: заявка закрыта сразу, деньги на балансе.
  if (result.status === 'rejected') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center space-y-3 py-4">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(240,176,78,0.15)] text-[#f0b04e] flex items-center justify-center">
            <AlertCircle className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
            <p className="text-sm text-muted-foreground mt-1">
              Перевод не прошёл, деньги вернулись на баланс. Откройте @xRocket, нажмите «Старт» и оформите вывод заново.
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold">
          Понятно
        </button>
      </div>
    );
  }

  // Исполнено без ссылки на чек (xRocket, TON): деньги уже у партнёра.
  if (result.auto_executed) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center space-y-3 py-4">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(56,224,165,0.14)] text-[#38e0a5] flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
            <p className="text-sm text-muted-foreground mt-1">
              {result.external_ref?.startsWith('xrocket:')
                ? 'Вывод выполнен: USDT уже в вашем кошельке @xRocket.'
                : 'Вывод выполнен, проверьте кошелёк.'}
            </p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold">
          Готово
        </button>
      </div>
    );
  }

  // Перевод ушёл или уходит (TON ждёт подтверждения сети), или проверяем, дошёл ли он.
  // Раньше без `auto_executed` экран обещал «администратор рассмотрит», а деньги уже в пути.
  if (result.transfer_state) {
    const text =
      result.transfer_state === 'unknown'
        ? 'Проверяем, дошёл ли перевод. Повторно деньги не отправим, пока не убедимся. Статус будет в разделе «История выводов».'
        : result.method === 'ton'
          ? 'Перевод отправлен в сеть TON. Как только сеть его подтвердит, в «Истории выводов» появится «Выплачено». Обычно это пара минут.'
          : result.method === 'xrocket'
            ? 'Перевод в @xRocket отправляется. Статус будет в разделе «История выводов».'
            : ['trc20', 'bep20', 'ton_usdt', 'usdt_sol', 'usdc_sol'].includes(result.method ?? '')
              ? 'Перевод отправлен. Деньги придут на адрес после подтверждения сети, обычно за несколько минут. Когда дойдёт, бот пришлёт ссылку на транзакцию.'
            : 'Чек выписывается. Ссылка на него появится в разделе «История выводов».';
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center space-y-3 py-4">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.07] text-muted-foreground flex items-center justify-center">
            <Clock className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
            <p className="text-sm text-muted-foreground mt-1">{text}</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold">
          Готово
        </button>
      </div>
    );
  }

  if (result.auto_error) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center text-center space-y-3 py-4">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(240,176,78,0.15)] text-[#f0b04e] flex items-center justify-center">
            <AlertCircle className="w-8 h-8" />
          </div>
          <div>
            <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
            <p className="text-sm text-muted-foreground mt-1">
              Авто-выплата не прошла. Заявка в очереди — администратор обработает вручную.
            </p>
            <p className="text-[11px] text-[#f0b04e] mt-2 italic">{result.auto_error.slice(0, 120)}</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold">
          Понятно
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center text-center space-y-3 py-4">
        <div className="w-16 h-16 rounded-2xl bg-white/[0.07] text-muted-foreground flex items-center justify-center">
          <Clock className="w-8 h-8" />
        </div>
        <div>
          <div className="font-display text-[26px] font-bold nums">{result.amount_rub.toLocaleString('ru')} ₽</div>
          <p className="text-sm text-muted-foreground mt-1">
            Заявка отправлена. Администратор рассмотрит её в течение 24 часов. Статус будет в разделе «История выводов».
          </p>
        </div>
      </div>
      <button type="button" onClick={onClose} className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold">
        Готово
      </button>
    </div>
  );
}

/**
 * Балансовая карточка (сумма + две кнопки) и история операций. Подписи
 * «Баланс», «Вывести деньги», «История» статичные — рисуем их настоящими,
 * плейсхолдером кроем только сумму и строки истории.
 */
function FinanceSkeleton() {
  const { t } = useTranslation();
  return (
    <SkeletonPage className="space-y-5">
      <MoneySwitch />
      <SkeletonCard className="rounded-[22px] p-5">
        <div className="text-[12.5px] text-dim">{t('Finance balance')}</div>
        <Skeleton className="h-[40px] w-48 mt-1.5 rounded-xl" />
        <Skeleton className="h-[50px] w-full mt-[18px] rounded-[16px]" />
        <Skeleton className="h-[45px] w-full mt-2.5 rounded-[16px]" />
      </SkeletonCard>
      <div>
        <div className="text-[12.5px] text-dim mb-2.5 px-1">{t('History')}</div>
        <div className="flex flex-col gap-[9px]">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} className="rounded-[16px] min-h-0 p-[14px] flex items-center gap-3">
              <Skeleton className="w-9 h-9 rounded-[11px] shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <SkeletonLine className="w-1/2" />
                <SkeletonLine size="xs" className="w-1/3" />
              </div>
              <SkeletonLine size="lg" className="w-14 shrink-0" />
            </SkeletonCard>
          ))}
        </div>
      </div>
    </SkeletonPage>
  );
}
